<template>
  <div id="mapWrapper">
    <div id="map">
      <DeviceSearch />
    </div>
    <MLPassagesPanel :visible="showMLPanel" />
    <DownloadSessionPanel />
    <div
      id="popupContainer"
      style="
        position: absolute;
        left: -9999px;
        top: -9999px;
        visibility: hidden;
      "
    >
      <DeviceInfo ref="deviceInfoRef" />
      <!-- this is used as a reference for popups -->
      <MarkerActions ref="markerActionsRef" />
      <!-- this is used as a reference for popups -->
    </div>
  </div>
</template>

<script lang="ts">
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import 'leaflet.awesome-markers/dist/leaflet.awesome-markers.css';
import 'leaflet.awesome-markers';
import 'leaflet-extra-markers/dist/css/leaflet.extra-markers.min.css';
import 'leaflet-extra-markers';
import 'leaflet-kml';
import 'leaflet-lasso';
import { defineComponent, markRaw, nextTick } from 'vue';
import { Device } from '../models/Device';
import { ActivePigRun, PipelineSectionMarker } from '../models/ActivePigRun';
import { PigRunMapEntry } from '../models/PigRunState';
import PigRunCommon from '../utils/PigRunCommon';
import DeviceSearch from '@/components/DeviceSearch.vue';
import DeviceInfo from '@/components/DeviceInfo.vue';
import MarkerActions from '@/components/MarkerActions.vue';
import MapCommon from '../utils/MapCommon';
import MLPassagesPanel from '@/components/MLPassagesPanel.vue';
import DownloadSessionPanel from '@/components/DownloadSessionPanel.vue';
import { hasAnyRole } from '@/utils/AuthGuard';

export default defineComponent({
  name: 'LeafletMap',
  components: {
    DeviceSearch,
    DeviceInfo,
    MarkerActions,
    MLPassagesPanel,
    DownloadSessionPanel,
  },
  data() {
    return {
      deviceLayers: {} as { [key: string]: L.Marker },
      pipelineLayerGroups: {} as { [pigRunId: number]: L.LayerGroup },
      markerIconLayers: {} as {
        [pigRunId: number]: { [markerId: number]: L.Marker };
      },
      pigIconLayers: {} as { [pigRunId: number]: L.Marker },
      pigRunLayerNames: {} as { [pigRunId: number]: string },
      initialPigRunZoomDone: false,
      showMLPanel: false,
      followPig: false,
      mlToggleLayer: null as any,
      followPigLayer: null as any,
      secondaryLayerControl: null as any,
      unsubscribe: (() => {}) as () => void,
      unsubscribeMutation: (() => {}) as () => void,
      activeHeartbeatUids: new Set<number>(),
      activeAlertUids: new Set<number>(),
      // uid -> current detection count, so we can re-render the badge
      // after setIcon recreates the marker DOM.
      detectionCounts: {} as Record<number, number>,
    };
  },
  computed: {
    leftDrawerOpen(): boolean {
      return this.$store.state.leftDrawerOpen;
    },
    selectedDevice(): Device {
      return this.$store.state.device.selectedDevice;
    },
    selectedMarker(): any {
      return this.$store.state.selectedMarker;
    },
    devices(): Device[] {
      return this.$store.state.device.devices;
    },

    activePigRuns(): ActivePigRun[] {
      return this.$store.getters.activePigRunsList;
    },
    pigRunEntries(): PigRunMapEntry[] {
      const pigRuns = this.$store.state.pigRun.pigRuns as Record<
        number,
        PigRunMapEntry
      >;
      const visibleIds = this.$store.state.pigRun.visiblePigRunIds as number[];
      return visibleIds
        .filter((id: number) => pigRuns[id])
        .map((id: number) => pigRuns[id]);
    },
    map(): any {
      return this.$store.state.map;
    },
    showDeviceInfo(): boolean {
      return this.$store.state.showDeviceInfo;
    },
  },
  watch: {
    leftDrawerOpen() {
      this.refreshMap();
    },
    selectedDevice(value: Device, oldValue: Device) {
      if (!value || !oldValue) return;
      if (value.uid !== oldValue.uid) {
        if (value.uid) {
          if (!this.leftDrawerOpen || !this.showDeviceInfo) {
            // show a popup if the drawer is closed
            const deviceInfoEl = (this.$refs.deviceInfoRef as any)
              ?.$el as HTMLElement;
            if (deviceInfoEl) {
              this.deviceLayers[value.uid]
                .bindPopup(deviceInfoEl, {
                  minWidth: 300,
                })
                .openPopup();
            }
          }
          if (
            value.lat &&
            value.lon &&
            value.lat !== 0 &&
            value.lon !== 0 &&
            Object.prototype.hasOwnProperty.call(this.deviceLayers, value.uid)
          ) {
            const iconElement = this.deviceLayers[value.uid].getElement();
            if (iconElement) {
              iconElement.classList.add('agmIconSelected');
            }
          } else {
            this.$store.dispatch('notify', 'Device does not have a location');
          }
        }
        if (
          oldValue.uid &&
          Object.prototype.hasOwnProperty.call(this.deviceLayers, oldValue.uid)
        ) {
          const iconElement = this.deviceLayers[oldValue.uid].getElement();
          if (iconElement) {
            iconElement.classList.remove('agmIconSelected');
          }
          this.deviceLayers[oldValue.uid].unbindPopup();
        }
      }
    },
    selectedMarker(value: any) {
      this.map.closePopup();
      const markerActionsEl = (this.$refs.markerActionsRef as any)
        ?.$el as HTMLElement;
      if (
        value &&
        Object.prototype.hasOwnProperty.call(value, 'name') &&
        markerActionsEl
      ) {
        // New shape: value has pigRunId directly
        if (value.pigRunId != null) {
          const layer =
            this.markerIconLayers[value.pigRunId]?.[
              value.pipelineSectionMarkerId
            ];
          if (layer) {
            layer.bindPopup(markerActionsEl, { minWidth: 300 });
            // Defer openPopup until Vue re-renders MarkerActions with the new marker data
            nextTick(() => {
              layer.openPopup();
              // Force Leaflet to re-measure after Vue has fully rendered the content
              nextTick(() => {
                const popup = layer.getPopup();
                if (popup && popup.isOpen()) popup.update();
              });
            });
            this.$store.dispatch('passRunId', value.pigRunId);
            if (this.leftDrawerOpen) {
              this.$store.dispatch('ClearCompletedRun');
            }
            return;
          }

          // Fallback: marker from a completed pig run preview (not in markerIconLayers)
          if (value.lat && value.lon) {
            nextTick(() => {
              L.popup({ minWidth: 300 })
                .setLatLng(L.latLng(value.lat, value.lon))
                .setContent(markerActionsEl)
                .openOn(this.map);
              nextTick(() => {
                if (this.map._popup && this.map._popup.isOpen()) {
                  this.map._popup.update();
                }
              });
            });
            return;
          }
        }
        // Fallback: legacy AgmMarker shape (lat/lon matching)
        for (const run of this.activePigRuns) {
          if (!run.markers) continue;
          const found = run.markers.some(
            (m: PipelineSectionMarker) =>
              m.name === value.name &&
              m.latitude === value.lat &&
              m.longitude === value.lon
          );
          if (found) {
            const layer =
              this.markerIconLayers[run.pigRunId]?.[
                value.pipelineSectionMarkerId
              ];
            if (layer) {
              layer.bindPopup(markerActionsEl, { minWidth: 300 });
              nextTick(() => {
                layer.openPopup();
                nextTick(() => {
                  const popup = layer.getPopup();
                  if (popup && popup.isOpen()) popup.update();
                });
              });
            }
            this.$store.dispatch('passRunId', run.pigRunId);
            if (this.leftDrawerOpen) {
              this.$store.dispatch('ClearCompletedRun');
            }
            return;
          }
        }
      }
      if (this.leftDrawerOpen) {
        this.$store.dispatch('ClearCompletedRun');
      }
    },
    async devices(value: Device[], oldValue: Device[]) {
      if (hasAnyRole(this.$store.state.user, 'viewer')) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
      if (oldValue.length > 0) {
        this.showDevices(false);
      } else {
        this.showDevices(true);
      }
    },

    pigRunEntries(newEntries: PigRunMapEntry[], oldEntries: PigRunMapEntry[]) {
      this.plotActivePigRuns(newEntries);
      // Re-evaluate device icons when pig runs change (markers affect proximity)
      this.refreshDeviceIconsForProximity();
      if (
        !this.secondaryLayerControl ||
        !this.mlToggleLayer ||
        !this.followPigLayer
      )
        return;
      if (oldEntries.length === 0 && newEntries.length > 0) {
        this.secondaryLayerControl.addOverlay(
          this.mlToggleLayer,
          'Show ML Verified Passages'
        );
        this.secondaryLayerControl.addOverlay(
          this.followPigLayer,
          'Follow Pig'
        );
      } else if (oldEntries.length > 0 && newEntries.length === 0) {
        this.secondaryLayerControl.removeLayer(this.mlToggleLayer);
        this.secondaryLayerControl.removeLayer(this.followPigLayer);
      }
    },
  },
  mounted() {
    this.createMap();
    this.$store.dispatch('initSignalR');

    // Plot any pig runs that were already loaded before mount
    if (this.pigRunEntries.length > 0) {
      this.plotActivePigRuns(this.pigRunEntries);
    }

    this.unsubscribe = this.$store.subscribeAction(
      (action: any, state: any) => {
        if (action.type === 'zoomToSelected') {
          this.zoomToSelected();
        }
        if (
          action.type === 'updatePigLoc' ||
          action.type === 'updatePigLocV2'
        ) {
          this.movePigIcon(action.payload.pigRunId);
          if (this.followPig) {
            const selectedId = this.$store.state.pigRun.selectedPigRunId;
            if (selectedId != null && selectedId === action.payload.pigRunId) {
              this.goToLatLon({
                lat: action.payload.lat,
                lon: action.payload.lon,
              });
            }
          }
        }
        if (action.type === 'passageRecieved') {
          this.addAlert(action.payload.uid);
        }
        if (action.type === 'updateDetectionCount') {
          this.addDetectionCount(
            action.payload.uid,
            action.payload.detectionCount
          );
        }
        if (action.type === 'heartbeatBatchReceived') {
          for (const data of action.payload as any[]) {
            const uid = String(data.uid);
            if (Object.prototype.hasOwnProperty.call(this.deviceLayers, uid)) {
              this.deviceLayers[uid].setLatLng(L.latLng(data.lat, data.lon));
            } else if (data.lat && data.lon) {
              this.addDeviceToMap(data.uid, data.lat, data.lon, true, false);
            }
            this.flashHeartbeat(data.uid);
          }
        }
        if (action.type === 'goToLatLon') {
          this.goToLatLon(action.payload);
        }
        if (action.type === 'selectPigRunLayer') {
          const targetId: number = action.payload;
          const targetLayer = this.pipelineLayerGroups[targetId];
          const map = this.map;
          if (!targetLayer || !map) return;
          // Remove all other pig run layers from the map so the Leaflet layer
          // control's radio reflects only the target. The target's radio will
          // appear checked because it's the only pig run layer still on the map.
          for (const [idStr, layer] of Object.entries(
            this.pipelineLayerGroups
          )) {
            if (Number(idStr) !== targetId && map.hasLayer(layer as any)) {
              (layer as any).removeFrom(map);
            }
          }
          if (!map.hasLayer(targetLayer)) {
            targetLayer.addTo(map);
          }
        }
        if (action.type === 'updateDeviceIcon') {
          this.updateDeviceIcon(
            action.payload.uid,
            action.payload.isConnected,
            action.payload.isIridium
          );
        }
        if (action.type === 'updateStatus') {
          const uid = action.payload.uid;
          const device = this.$store.state.device.devices.find(
            (d: Device) => d.uid === uid
          );
          if (device) {
            this.updateDeviceIcon(
              uid,
              action.payload.isConnected,
              device.isIridium
            );
          }
        }
      }
    );

    this.unsubscribeMutation = this.$store.subscribe((mutation: any) => {
      if (mutation.type === 'UPDATE_MARKER') {
        const { pigRunId, pipelineSectionMarkerId, fields } = mutation.payload;
        if (fields.trackState != null) {
          const layer =
            this.markerIconLayers[pigRunId]?.[pipelineSectionMarkerId];
          if (layer) {
            const markerState = this.$store.state.pigRun.pigRuns[
              pigRunId
            ]?.markers?.find(
              (m: any) => m.pipelineSectionMarkerId === pipelineSectionMarkerId
            );
            const hasVerified = markerState?.hasVerifiedPassage ?? false;
            const icon =
              fields.trackState === 'S'
                ? PigRunCommon.skipIcon
                : hasVerified
                ? PigRunCommon.trackIcon
                : PigRunCommon.trackIcon_2;
            layer.setIcon(icon);
          }
        }
      }
      if (mutation.type === 'UPDATE_MARKER_VERIFICATION') {
        const { pigRunId, pipelineSectionMarkerId, hasVerifiedPassage } =
          mutation.payload;
        const layer =
          this.markerIconLayers[pigRunId]?.[pipelineSectionMarkerId];
        if (layer) {
          const icon = hasVerifiedPassage
            ? PigRunCommon.trackIcon
            : PigRunCommon.trackIcon_2;
          layer.setIcon(icon);
        }
      }
      if (mutation.type === 'SET_STREAMING_UID') {
        this.updateStreamingIcon(mutation.payload as number | null);
      }
    });
  },
  beforeUnmount() {
    this.unsubscribe();
    this.unsubscribeMutation();
  },
  methods: {
    getMap() {
      return this.$store.state.map;
    },
    isDeviceNearAnyMarker(lat: number, lon: number): boolean {
      const MARKER_PROXIMITY_THRESHOLD_M = 100;
      const pigRuns = this.$store.state.pigRun.pigRuns as Record<
        number,
        PigRunMapEntry
      >;
      const deviceLatLng = L.latLng(lat, lon);
      for (const entry of Object.values(pigRuns)) {
        for (const marker of entry.markers) {
          if (!marker.latitude || !marker.longitude) continue;
          const distance = deviceLatLng.distanceTo(
            L.latLng(marker.latitude, marker.longitude)
          );
          if (distance <= MARKER_PROXIMITY_THRESHOLD_M) {
            return true;
          }
        }
      }
      return false;
    },
    refreshDeviceIconsForProximity() {
      for (const uid of Object.keys(this.deviceLayers).map(Number)) {
        const device: any = this.$store.state.device.devices.find(
          (d: any) => d.uid === uid
        );
        if (device) {
          this.updateDeviceIcon(uid, device.isConnected, device.isIridium);
        }
      }
    },
    addDeviceToMap(
      uid: number,
      lat: number | undefined,
      lon: number | undefined,
      isConnected: boolean | undefined,
      isIridium: boolean | undefined
    ) {
      const device: any = this.$store.state.device.devices.find(
        (d: any) => d.uid === uid
      );
      const isType62 = device && device.deviceType === 'AGMVECTOR';

      const isNearMarker = lat && lon && this.isDeviceNearAnyMarker(lat, lon);

      let className: string;
      if (isNearMarker && isConnected) {
        className = isIridium
          ? isType62
            ? 'agmIconAssignedPigRunIridium62'
            : 'agmIconAssignedPigRunIridium'
          : isType62
          ? 'agmIconAssignedPigRun62'
          : 'agmIconAssignedPigRun';
      } else if (isNearMarker && !isConnected) {
        className = isIridium
          ? isType62
            ? 'agmIconOfflinePigRunIridium62'
            : 'agmIconOfflinePigRunIridium'
          : isType62
          ? 'agmIconOfflinePigRun62'
          : 'agmIconOfflinePigRun';
      } else {
        className = isIridium
          ? isConnected
            ? isType62
              ? 'agmIconOnlineIridium62'
              : 'agmIconOnlineIridium'
            : isType62
            ? 'agmIconOfflineIridium62'
            : 'agmIconOfflineIridium'
          : isConnected
          ? isType62
            ? 'agmIconOnline62'
            : 'agmIconOnline'
          : isType62
          ? 'agmIconOffline62'
          : 'agmIconOffline';
      }

      const myIcon = L.divIcon({
        className,
        iconSize: [32, 32],
        html: '',
      });
      // Render online devices above offline ones so they aren't covered
      // when markers overlap. Leaflet stacks higher zIndexOffset on top.
      const zIndexOffset = isConnected ? 1000 : 0;
      if (lat && lon) {
        if (!Object.prototype.hasOwnProperty.call(this.deviceLayers, uid)) {
          const layer = L.marker([lat, lon], {
            icon: myIcon,
            zIndexOffset,
          })
            .addTo(this.getMap())
            .on('click', () => {
              this.$store.dispatch('selectDevice', { uid });
            });
          this.deviceLayers[uid] = markRaw(layer);
          this.$store.dispatch('setLeafletLayer', {
            uid,
            leafletId: (layer as any)._leaflet_id,
          });
        } else {
          this.deviceLayers[uid].setLatLng([lat, lon]);
          this.deviceLayers[uid].setIcon(myIcon);
          this.deviceLayers[uid].setZIndexOffset(zIndexOffset);
          // setIcon replaces the marker's DOM element, which wipes any
          // child decorations we appended. Re-apply them.
          this.restoreDeviceDecorations(uid);
        }
      }
    },
    addAlert(alertUid: number) {
      // Remove any existing alert for this UID first.
      this.removeAlert(alertUid);
      if (!Object.prototype.hasOwnProperty.call(this.deviceLayers, alertUid)) {
        return;
      }
      const template = document.createElement('template');
      template.innerHTML = '<div class="passageAlarm"></div>'.trim();
      const iconElement = this.deviceLayers[alertUid].getElement();
      if (!iconElement) return;
      this.activeAlertUids.add(alertUid);
      if (this.activePigRuns.length) {
        setTimeout(() => {
          iconElement.appendChild(template.content.firstChild as Node);
        }, 1000);
      } else {
        iconElement.appendChild(template.content.firstChild as Node);
      }
    },
    removeAlert(uid: number) {
      if (Object.prototype.hasOwnProperty.call(this.deviceLayers, uid)) {
        const iconElement = this.deviceLayers[uid].getElement();
        if (iconElement) {
          iconElement.childNodes.forEach((currentNode: any) => {
            if (currentNode.className === 'passageAlarm') {
              iconElement.removeChild(currentNode);
            }
          });
        }
      }
      this.activeAlertUids.delete(uid);
    },
    // Re-apply decorations (streaming icon, detection-count badge) that
    // live as DOM children of the marker element. Leaflet's setIcon()
    // recreates the element, so these need to be restored.
    restoreDeviceDecorations(uid: number) {
      if (this.$store.state.realtimeStreaming?.streamingUid === uid) {
        this.updateStreamingIcon(uid);
      }
      const count = this.detectionCounts[uid];
      if (count != null) {
        this.addDetectionCount(uid, count);
      }
    },
    updateStreamingIcon(newUid: number | null) {
      // Remove icon from all devices first
      for (const [uid, layer] of Object.entries(this.deviceLayers)) {
        const el = layer.getElement();
        if (!el) continue;
        el.querySelectorAll('.streamingIcon').forEach((node: Element) =>
          node.remove()
        );
      }
      // Add icon to the new streaming device
      if (newUid != null && this.deviceLayers[newUid]) {
        const el = this.deviceLayers[newUid].getElement();
        if (el) {
          const icon = document.createElement('div');
          icon.className = 'streamingIcon';
          icon.textContent = '\uD83C\uDFA7'; // headphone emoji
          el.appendChild(icon);
        }
      }
    },
    addDetectionCount(dcUid: number, detectionCount: number) {
      // Remove any existing count for this UID first, then add the new one.
      this.removeDetectionCount(dcUid);
      if (!Object.prototype.hasOwnProperty.call(this.deviceLayers, dcUid)) {
        return;
      }
      this.detectionCounts[dcUid] = detectionCount;
      const template = document.createElement('template');
      let styleModifier = '';
      if (detectionCount.toString().length === 3) {
        styleModifier = 'style="width: 20px"';
      } else if (detectionCount.toString().length === 4) {
        styleModifier = 'style="width: 26px"';
      }
      template.innerHTML =
        '<div class="detectCount"' +
        styleModifier +
        '>' +
        detectionCount +
        '</div>'.trim();
      const iconElement = this.deviceLayers[dcUid].getElement();
      if (!iconElement) return;
      if (this.activePigRuns.length) {
        setTimeout(() => {
          iconElement.appendChild(template.content.firstChild as Node);
        }, 1000);
      } else {
        iconElement.appendChild(template.content.firstChild as Node);
      }
    },
    removeDetectionCount(uid: number) {
      delete this.detectionCounts[uid];
      if (Object.prototype.hasOwnProperty.call(this.deviceLayers, uid)) {
        const iconElement = this.deviceLayers[uid].getElement();
        if (iconElement) {
          iconElement.childNodes.forEach((currentNode: any) => {
            if (currentNode.className === 'detectCount') {
              iconElement.removeChild(currentNode);
            }
          });
        }
      }
    },
    flashHeartbeat(uid: number) {
      // Skip if a heart animation is already playing for this UID.
      if (this.activeHeartbeatUids.has(uid)) return;
      if (Object.prototype.hasOwnProperty.call(this.deviceLayers, uid)) {
        const template = document.createElement('template');
        template.innerHTML = '<div class="heart"></div>'.trim();
        const iconElement = this.deviceLayers[uid].getElement();
        if (iconElement) {
          this.activeHeartbeatUids.add(uid);
          iconElement.appendChild(template.content.firstChild as Node);
          setTimeout(() => {
            iconElement.childNodes.forEach((currentNode: any) => {
              if (currentNode.className === 'heart') {
                iconElement.removeChild(currentNode);
              }
            });
            this.activeHeartbeatUids.delete(uid);
          }, 2000);
        }
      }
    },
    zoomToSelected() {
      if (
        this.selectedDevice &&
        this.selectedDevice.lat &&
        this.selectedDevice.lon
      ) {
        const pt: L.LatLng = L.latLng(
          this.selectedDevice.lat,
          this.selectedDevice.lon
        );
        this.getMap().setView(pt, 15);
      }
    },
    updateDeviceIcon(uid: number, isConnected: boolean, isIridium: boolean) {
      if (Object.prototype.hasOwnProperty.call(this.deviceLayers, uid)) {
        const device: any = this.$store.state.device.devices.find(
          (d: any) => d.uid === uid
        );
        const isType62 = device && device.deviceType === 'AGMVECTOR';
        const isNearMarker =
          device?.lat &&
          device?.lon &&
          this.isDeviceNearAnyMarker(device.lat, device.lon);

        let icon;
        if (isNearMarker && isConnected) {
          icon = isIridium
            ? isType62
              ? MapCommon.assignedIridiumOnlineIcon62
              : MapCommon.assignedIridiumOnlineIcon
            : isType62
            ? MapCommon.assignedOnlineIcon62
            : MapCommon.assignedOnlineIcon;
        } else if (isNearMarker && !isConnected) {
          icon = isIridium
            ? isType62
              ? MapCommon.assignedIridiumOfflineIcon62
              : MapCommon.assignedIridiumOfflineIcon
            : isType62
            ? MapCommon.assignedOfflineIcon62
            : MapCommon.assignedOfflineIcon;
        } else if (isIridium) {
          icon = isConnected
            ? isType62
              ? MapCommon.agmIconOnlineIridium62
              : MapCommon.agmIconOnlineIridium
            : isType62
            ? MapCommon.agmIconOfflineIridium62
            : MapCommon.agmIconOfflineIridium;
        } else {
          icon = isConnected
            ? isType62
              ? MapCommon.agmIconOnline62
              : MapCommon.agmIconOnline
            : isType62
            ? MapCommon.agmIconOffline62
            : MapCommon.agmIconOffline;
        }
        this.deviceLayers[uid].setIcon(icon);
        this.deviceLayers[uid].setZIndexOffset(isConnected ? 1000 : 0);
        // setIcon recreates the marker DOM, so restore decorations.
        this.restoreDeviceDecorations(uid);
      }
    },
    createMap() {
      const defaultCenter: L.LatLng = L.latLng(44.9778, -93.265044);
      const defaultZoom = 4;

      const topoLayer = L.tileLayer(
        'https://services.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}.jpg',
        {
          attribution:
            '<span>Esri, HERE, Garmin, Intermap, increment P Corp., GEBCO, USGS, FAO, NPS, NRCAN, GeoBase, IGN, Kadaster NL, Ordnance Survey, Esri Japan, METI, Esri China (Hong Kong), (c) OpenStreetMap contributors, and the GIS User Community</span>',
          id: 'topo',
          crossOrigin: 'anonymous',
        }
      );

      const satelliteLayer = L.tileLayer(
        'https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}.jpg',
        {
          attribution:
            '<span>Esri, DigitalGlobe, GeoEye, Earthstar Geographics, CNES/Airbus DS, USDA, USGS, AeroGRID, IGN, and the GIS User Community</span>',
          id: 'satelite',
          crossOrigin: 'anonymous',
        }
      );

      const googleStreets = L.tileLayer(
        'https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}',
        {
          maxZoom: 20,
          subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
          crossOrigin: 'anonymous',
        }
      );

      const googleHybrid = L.tileLayer(
        'https://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}',
        {
          maxZoom: 20,
          subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
          crossOrigin: 'anonymous',
        }
      );

      const lMap: L.Map = L.map('map', {
        layers: [googleHybrid],
        maxZoom: 20,
        minZoom: 3,
        maxBounds: [
          [90, -180],
          [-90, 180],
        ],
      }).setView(defaultCenter, defaultZoom);

      this.$store.state.map = markRaw(lMap);

      const baseMaps: any = {
        Basic: topoLayer,
        Streets: googleStreets,
        Satellite: satelliteLayer,
        Default: googleHybrid,
      };

      const layerControl = new L.Control.Layers(
        baseMaps,
        {},
        { position: 'topleft' }
      ) as L.Control.Layers;
      const secondaryLayerControl = new L.Control.Layers(
        {},
        {},
        { collapsed: false, position: 'topleft' }
      ) as L.Control.Layers;
      secondaryLayerControl.addBaseLayer(
        L.layerGroup([]),
        'Show No Active Runs'
      );

      const mlToggleLayer = L.layerGroup([]);
      this.mlToggleLayer = markRaw(mlToggleLayer);

      const followPigLayer = L.layerGroup([]);
      this.followPigLayer = markRaw(followPigLayer);
      this.secondaryLayerControl = markRaw(secondaryLayerControl);

      if (this.pigRunEntries.length > 0) {
        secondaryLayerControl.addOverlay(
          mlToggleLayer,
          'Show ML Verified Passages'
        );
        secondaryLayerControl.addOverlay(followPigLayer, 'Follow Pig');
      }

      lMap.on('overlayadd', (e: any) => {
        if (e.name === 'Show ML Verified Passages') {
          this.showMLPanel = true;
          this.$store.commit('SET_SHOW_ML_PANEL', true);
        }
        if (e.name === 'Follow Pig') {
          this.followPig = true;
          // Immediately pan to the selected pig run (or first visible with location)
          const selectedId = this.$store.state.pigRun.selectedPigRunId;
          const selectedEntry =
            selectedId != null
              ? this.pigRunEntries.find(
                  (entry: any) =>
                    entry.pigRun.pigRunId === selectedId &&
                    entry.pigLocation?.lat &&
                    entry.pigLocation?.lon
                )
              : null;
          const targetEntry = (selectedEntry ||
            this.pigRunEntries.find(
              (entry: any) => entry.pigLocation?.lat && entry.pigLocation?.lon
            )) as any;
          if (targetEntry) {
            this.goToLatLon({
              lat: targetEntry.pigLocation.lat,
              lon: targetEntry.pigLocation.lon,
            });
          }
        }
      });
      lMap.on('overlayremove', (e: any) => {
        if (e.name === 'Show ML Verified Passages') {
          this.showMLPanel = false;
          this.$store.commit('SET_SHOW_ML_PANEL', false);
        }
        if (e.name === 'Follow Pig') this.followPig = false;
      });

      // baselayerchange fires reliably whenever the user clicks a pig run radio
      // button — including the first click after initial load when all layers
      // are already on the map and the layerGroup 'add' event doesn't re-fire.
      lMap.on('baselayerchange', (e: any) => {
        const entry = Object.entries(this.pipelineLayerGroups).find(
          ([, layer]) => layer === e.layer
        );
        if (entry) {
          this.$store.commit('SET_SELECTED_PIG_RUN_ID', Number(entry[0]));
        } else {
          // "Show No Active Runs" was selected — deselect all pig runs and hide their layers
          this.$store.commit('CLEAR_SELECTED_PIG_RUN_ID');
          for (const layer of Object.values(this.pipelineLayerGroups)) {
            if (lMap.hasLayer(layer as any)) {
              (layer as any).removeFrom(lMap);
            }
          }
        }
      });

      this.$store.dispatch('setLayerControl', markRaw(secondaryLayerControl));
      this.createHomeControl();
      layerControl.addTo(lMap);
      secondaryLayerControl.addTo(lMap);
    },
    zoomHome() {
      const latLngs: L.LatLng[] = [];
      for (const uid of Object.keys(this.deviceLayers)) {
        latLngs.push(this.deviceLayers[uid].getLatLng());
      }
      if (latLngs.length > 0) {
        this.getMap().fitBounds(L.latLngBounds(latLngs), { maxZoom: 19 });
      } else {
        this.$store.dispatch('notify', 'No device locations found');
      }
    },
    createHomeControl() {
      const self = this;
      const HomeControl = L.Control.extend({
        onAdd(map: L.Map) {
          const container = L.DomUtil.create(
            'div',
            'leaflet-bar leaflet-control leafletControlHome'
          );
          container.style.backgroundImage = 'url(home-24px.svg)';
          container.style.backgroundColor = 'white';
          container.style.backgroundPosition = 'center';
          container.style.width = '26px';
          container.style.height = '26px';
          container.onclick = () => {
            self.zoomHome();
          };
          return container;
        },
        onRemove(map: L.Map) {},
      });
      const hc = new (HomeControl as any)({ position: 'topleft' }) as L.Control;
      hc.addTo(this.getMap() as L.Map);
    },
    pigRunLabel(pigRun: ActivePigRun): string {
      const toolName = pigRun.tools?.[0]?.name;
      return toolName ? `${toolName} on ${pigRun.name}` : pigRun.name;
    },
    movePigIcon(pigRunId: number) {
      const entry = (
        this.$store.state.pigRun.pigRuns as Record<number, PigRunMapEntry>
      )[pigRunId];
      if (!entry?.pigLocation) return;

      const { lat, lon } = entry.pigLocation;
      if (this.pigIconLayers[pigRunId]) {
        this.pigIconLayers[pigRunId].setLatLng([lat, lon]);
      }
    },
    removePigRunLayers(
      pigRunId: number,
      map: L.Map,
      layerControl: L.Control.Layers | null
    ) {
      if (this.pipelineLayerGroups[pigRunId]) {
        this.pipelineLayerGroups[pigRunId].off();
        this.pipelineLayerGroups[pigRunId].removeFrom(map);
        if (layerControl) {
          layerControl.removeLayer(this.pipelineLayerGroups[pigRunId]);
        }
        delete this.pipelineLayerGroups[pigRunId];
        // .off() clears listeners before removeFrom, so the layerGroup's own
        // 'remove' event handler never fires here. Handle deselection directly.
        if (this.$store.state.pigRun.selectedPigRunId === pigRunId) {
          this.$store.commit('SET_SELECTED_PIG_RUN_ID', null);
        }
      }
      if (this.markerIconLayers[pigRunId]) {
        for (const layer of Object.values(this.markerIconLayers[pigRunId])) {
          layer.off('click');
        }
      }
      delete this.markerIconLayers[pigRunId];
      delete this.pigRunLayerNames[pigRunId];
      if (this.pigIconLayers[pigRunId]) {
        this.pigIconLayers[pigRunId].off('click');
        this.pigIconLayers[pigRunId].removeFrom(map);
        delete this.pigIconLayers[pigRunId];
      }
    },
    plotActivePigRuns(entries: PigRunMapEntry[]) {
      const map = this.getMap();
      if (!map) return;

      const layerControl = this.$store.state
        .layerControl as L.Control.Layers | null;

      // Remove layers for runs that are no longer visible
      const activeIds = new Set(entries.map((e) => e.pigRun.pigRunId));
      for (const id of Object.keys(this.pipelineLayerGroups)) {
        const numId = parseInt(id, 10);
        if (!activeIds.has(numId)) {
          this.removePigRunLayers(numId, map, layerControl);
        }
      }

      for (const entry of entries) {
        const pigRun = entry.pigRun;
        const sortedMarkers = entry.markers;

        // Skip re-draw if the layer group already exists for this run
        if (this.pipelineLayerGroups[pigRun.pigRunId]) {
          // If the marker count changed (e.g. dynamic marker added/removed),
          // tear down and rebuild the entire layer group
          const existingMarkerCount = Object.keys(
            this.markerIconLayers[pigRun.pigRunId] || {}
          ).length;
          if (existingMarkerCount !== sortedMarkers.length) {
            this.removePigRunLayers(pigRun.pigRunId, map, layerControl);
            // Fall through to full redraw below
          } else {
            // Update layer control label if the pig run name changed (e.g. after launch refresh)
            const label = this.pigRunLabel(pigRun);
            if (
              layerControl &&
              this.pigRunLayerNames[pigRun.pigRunId] &&
              this.pigRunLayerNames[pigRun.pigRunId] !== label
            ) {
              layerControl.removeLayer(
                this.pipelineLayerGroups[pigRun.pigRunId]
              );
              layerControl.addBaseLayer(
                this.pipelineLayerGroups[pigRun.pigRunId],
                label
              );
              this.pigRunLayerNames[pigRun.pigRunId] = label;
            }

            // Sync marker icons with current trackState
            const runMarkerLayers =
              this.markerIconLayers[pigRun.pigRunId] || {};
            for (const marker of sortedMarkers) {
              const layer = runMarkerLayers[marker.pipelineSectionMarkerId];
              if (!layer) continue;
              if (
                marker.description === 'LAUNCH' ||
                marker.description === 'RECEIVE'
              )
                continue;
              const icon =
                marker.trackState === 'S'
                  ? PigRunCommon.skipIcon
                  : marker.hasVerifiedPassage
                  ? PigRunCommon.trackIcon
                  : PigRunCommon.trackIcon_2;
              if (layer.options.icon !== icon) {
                layer.setIcon(icon);
              }
            }

            continue;
          }
        }

        const layerGroup = L.layerGroup();
        const markerLayers: { [markerId: number]: L.Marker } = {};

        // Build polyline from marker positions
        const latLngs: L.LatLng[] = sortedMarkers
          .filter((m) => m.latitude && m.longitude)
          .map((m) => L.latLng(m.latitude, m.longitude));

        if (latLngs.length >= 2) {
          L.polyline(latLngs, {
            color: '#1976D2',
            weight: 3,
            opacity: 0.8,
          }).addTo(layerGroup);
        }

        // Add marker icons and 100m circles
        for (const marker of sortedMarkers) {
          if (!marker.latitude || !marker.longitude) continue;

          const markerLatLng = L.latLng(marker.latitude, marker.longitude);

          L.circle(markerLatLng, {
            radius: 100,
            color: '#2196F3',
            fillColor: '#2196F3',
            fillOpacity: 0.1,
            weight: 1,
          }).addTo(layerGroup);

          let markerIcon: L.Icon | L.DivIcon;
          if (marker.description === 'LAUNCH') {
            markerIcon = PigRunCommon.launchIcon;
          } else if (marker.description === 'RECEIVE') {
            markerIcon = PigRunCommon.recieveIcon;
          } else if (marker.trackState === 'S') {
            markerIcon = PigRunCommon.skipIcon;
          } else {
            markerIcon = marker.hasVerifiedPassage
              ? PigRunCommon.trackIcon
              : PigRunCommon.trackIcon_2;
          }

          const leafletMarker = L.marker(markerLatLng, {
            icon: markerIcon,
          }).addTo(layerGroup);

          leafletMarker.bindTooltip(marker.name, {
            permanent: false,
            direction: 'top',
            offset: [0, -8],
          });

          // Dispatch rich marker payload so the popup has full context
          const clickPayload = {
            pigRunId: pigRun.pigRunId,
            pipelineSectionMarkerId: marker.pipelineSectionMarkerId,
            name: marker.name,
            lat: marker.latitude,
            lon: marker.longitude,
            trackState: marker.trackState,
            description: marker.description,
            autoVerify: marker.autoVerify,
          };
          leafletMarker.on('click', () => {
            this.$store.dispatch('setSelectedMarker', clickPayload);
          });

          markerLayers[marker.pipelineSectionMarkerId] = markRaw(leafletMarker);
        }

        this.pipelineLayerGroups[pigRun.pigRunId] = markRaw(layerGroup);
        this.markerIconLayers[pigRun.pigRunId] = markerLayers;

        // Place pig icon — use current pigLocation if available, otherwise launch marker
        const pigLoc = entry.pigLocation;
        let pigLatLng: L.LatLng | null = null;
        if (pigLoc) {
          pigLatLng = L.latLng(pigLoc.lat, pigLoc.lon);
        } else {
          const launchMarker = sortedMarkers.find(
            (m) => m.description === 'LAUNCH'
          );
          if (launchMarker && launchMarker.latitude && launchMarker.longitude) {
            pigLatLng = L.latLng(launchMarker.latitude, launchMarker.longitude);
          }
        }
        if (pigLatLng) {
          const pigMarker = L.marker(pigLatLng, {
            icon: PigRunCommon.pigIcon,
            zIndexOffset: 1000,
          }).addTo(layerGroup);

          pigMarker.bindTooltip(this.pigRunLabel(pigRun) + ' — Pig', {
            permanent: false,
            direction: 'top',
            offset: [0, -30],
          });

          // Proxy click to nearest pipeline section marker so LAUNCH/RECEIVE are reachable
          pigMarker.on('click', () => {
            const pigPos = pigMarker.getLatLng();
            let nearest: (typeof sortedMarkers)[0] | null = null;
            let nearestDist = Infinity;
            for (const m of sortedMarkers) {
              if (!m.latitude || !m.longitude) continue;
              const d = pigPos.distanceTo(L.latLng(m.latitude, m.longitude));
              if (d < nearestDist) {
                nearestDist = d;
                nearest = m;
              }
            }
            if (nearest && nearestDist <= 150) {
              this.$store.dispatch('setSelectedMarker', {
                pigRunId: pigRun.pigRunId,
                pipelineSectionMarkerId: nearest.pipelineSectionMarkerId,
                name: nearest.name,
                lat: nearest.latitude,
                lon: nearest.longitude,
                trackState: nearest.trackState,
                description: nearest.description,
                autoVerify: nearest.autoVerify,
              });
            }
          });

          this.pigIconLayers[pigRun.pigRunId] = markRaw(pigMarker);
        }

        // Track selection: on initial plot, auto-select the first pig run added.
        // Subsequent user selections are handled by the map's baselayerchange event.
        const runId = pigRun.pigRunId;
        layerGroup.on('add', () => {
          if (this.$store.state.pigRun.selectedPigRunId == null) {
            this.$store.commit('SET_SELECTED_PIG_RUN_ID', runId);
          }
          if (latLngs.length > 0) {
            setTimeout(() => {
              map.invalidateSize();
              map.fitBounds(L.latLngBounds(latLngs), {
                maxZoom: 15,
                padding: [50, 50],
              });
            }, 100);
          }
        });

        layerGroup.addTo(map);
        if (layerControl) {
          const label = this.pigRunLabel(pigRun);
          layerControl.addBaseLayer(layerGroup, label);
          this.pigRunLayerNames[pigRun.pigRunId] = label;
        }
      }

      // After all layers are added, ensure only the selected pig run's layer
      // is on the map. All layers are added to the map above so Leaflet can
      // register them in the control, but leaving multiple on simultaneously
      // causes the radio to mismatch the store's selectedPigRunId. Stripping
      // the non-selected ones here makes both sides agree on initial load.
      const selectedId = this.$store.state.pigRun.selectedPigRunId;
      if (selectedId != null) {
        for (const [idStr, layer] of Object.entries(this.pipelineLayerGroups)) {
          if (Number(idStr) !== selectedId && map.hasLayer(layer as any)) {
            (layer as any).removeFrom(map);
          }
        }
      }

      // Auto-zoom to fit all pipeline markers on initial plot only
      if (entries.length > 0 && !this.initialPigRunZoomDone) {
        this.initialPigRunZoomDone = true;
        const allLatLngs: L.LatLng[] = [];
        for (const entry of entries) {
          for (const marker of entry.markers) {
            if (marker.latitude && marker.longitude) {
              allLatLngs.push(L.latLng(marker.latitude, marker.longitude));
            }
          }
        }
        if (allLatLngs.length > 0) {
          setTimeout(() => {
            map.invalidateSize();
            map.fitBounds(L.latLngBounds(allLatLngs), {
              maxZoom: 15,
              padding: [50, 50],
            });
          }, 100);
        }
      }
    },
    showDevices(isZoom: boolean) {
      this.$store.state.device.devices.forEach((device: Device) => {
        if (hasAnyRole(this.$store.state.user, 'viewer')) {
          if (
            this.$store.state.activePigRunUidArrayWithNotification !=
              undefined &&
            this.$store.state.activePigRunUidArrayWithNotification.length > 0
          ) {
            if (
              this.$store.state.activePigRunUidArrayWithNotification.findIndex(
                (element: number) => element == device.uid
              ) != -1
            ) {
              this.addDeviceToMap(
                device.uid,
                device.lat,
                device.lon,
                device.isConnected,
                device.isIridium
              );
            }
          }
        } else {
          this.addDeviceToMap(
            device.uid,
            device.lat,
            device.lon,
            device.isConnected,
            device.isIridium
          );
        }
      });

      if (isZoom) {
        if (this.$store.state.device.devices.length > 0) {
          this.zoomHome();
        } else {
          this.$store.dispatch('notify', 'No devices found');
        }
      }
    },
    refreshMap() {
      if (this.getMap()) {
        this.getMap().invalidateSize();
      }
    },
    goToLatLon(queryParams: any) {
      const latLng = new L.LatLng(queryParams.lat, queryParams.lon);
      this.map.setView(latLng, 16);
    },
  },
});
</script>

<style scoped>
#mapWrapper {
  position: relative;
  height: 100vh;
  margin-top: -50px;
}
#map {
  position: absolute;
  top: 50px;
  bottom: 0;
  width: 100%;
}
.leafletControlHome {
  background-color: 'white';
  background-repeat: no-repeat;
  background-attachment: fixed;
  background-position: center;
}
</style>

<style>
.agmIconOnline {
  width: 32px;
  height: 32px;
  background-image: url('/greenCell.png');
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
}

.agmIconOffline {
  width: 32px;
  height: 32px;
  background-image: url('/greyCell.png');
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
}

.agmIconOfflinePigRun {
  width: 32px;
  height: 32px;
  background-image: url('/blueCell.png');
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
}

.agmIconAssignedPigRun {
  width: 32px;
  height: 32px;
  background-image: url('/redCell.png');
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
}

.agmIconOnlineIridium {
  width: 32px;
  height: 32px;
  background-image: url('/greenIridium.png');
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
}

.agmIconOfflineIridium {
  width: 32px;
  height: 32px;
  background-image: url('/greyIridium.png');
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
}

.agmIconOfflinePigRunIridium {
  width: 32px;
  height: 32px;
  background-image: url('/blueIridium.png');
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
}

.agmIconAssignedPigRunIridium {
  width: 32px;
  height: 32px;
  background-image: url('/redIridium.png');
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
}

/* ProductNumber 62 (ApexAgm20) - cellular */
.agmIconOnline62 {
  width: 32px;
  height: 32px;
  background-image: url('/greenCell_62.png');
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
}

.agmIconOffline62 {
  width: 32px;
  height: 32px;
  background-image: url('/greyCell_62.png');
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
}

.agmIconOfflinePigRun62 {
  width: 32px;
  height: 32px;
  background-image: url('/blueCell_62.png');
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
}

.agmIconAssignedPigRun62 {
  width: 32px;
  height: 32px;
  background-image: url('/redCell_62.png');
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
}

/* ProductNumber 62 (ApexAgm20) - iridium */
.agmIconOnlineIridium62 {
  width: 32px;
  height: 32px;
  background-image: url('/greenIridium_62.png');
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
}

.agmIconOfflineIridium62 {
  width: 32px;
  height: 32px;
  background-image: url('/greyIridium_62.png');
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
}

.agmIconOfflinePigRunIridium62 {
  width: 32px;
  height: 32px;
  background-image: url('/blueIridium_62.png');
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
}

.agmIconAssignedPigRunIridium62 {
  width: 32px;
  height: 32px;
  background-image: url('/redIridium_62.png');
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
}

/* .agmIconSelected {
  background-image: url('/yellow.png');
} */

.agmIconSelected {
  border-width: 2px;
  padding: 13px;
  border-color: #0ff;
  border-style: solid;
  border-radius: 2px;
}

.simpleMarker {
  background: #ac5fda;
  border-radius: 50%;
  box-shadow: 4px 4px 3px grey;
  -moz-box-shadow: 4px 4px 3px grey;
  -webkit-box-shadow: 4px 4px 3px grey;
}

.simpleMarkerTap {
  background: #d53e4f;
  border-radius: 50%;
  box-shadow: 4px 4px 3px grey;
  -moz-box-shadow: 4px 4px 3px grey;
  -webkit-box-shadow: 4px 4px 3px grey;
}

.simpleMarkerMagnet {
  background: #f46d43;
  border-radius: 50%;
  box-shadow: 4px 4px 3px grey;
  -moz-box-shadow: 4px 4px 3px grey;
  -webkit-box-shadow: 4px 4px 3px grey;
}

.simpleMarkerBend {
  background: #fdae61;
  border-radius: 50%;
  box-shadow: 4px 4px 3px grey;
  -moz-box-shadow: 4px 4px 3px grey;
  -webkit-box-shadow: 4px 4px 3px grey;
}

.simpleMarkerWeld {
  background: #fee08b;
  border-radius: 50%;
  box-shadow: 4px 4px 3px grey;
  -moz-box-shadow: 4px 4px 3px grey;
  -webkit-box-shadow: 4px 4px 3px grey;
}

.simpleMarkerAttachment {
  background: #e6f598;
  border-radius: 50%;
  box-shadow: 4px 4px 3px grey;
  -moz-box-shadow: 4px 4px 3px grey;
  -webkit-box-shadow: 4px 4px 3px grey;
}

.simpleMarkerTee {
  background: #abdda4;
  border-radius: 50%;
  box-shadow: 4px 4px 3px grey;
  -moz-box-shadow: 4px 4px 3px grey;
  -webkit-box-shadow: 4px 4px 3px grey;
}

.simpleMarkerPipe {
  background: #66c2a5;
  border-radius: 50%;
  box-shadow: 4px 4px 3px grey;
  -moz-box-shadow: 4px 4px 3px grey;
  -webkit-box-shadow: 4px 4px 3px grey;
}

.simpleMarkerOther {
  background: #3288bd;
  border-radius: 50%;
  box-shadow: 4px 4px 3px grey;
  -moz-box-shadow: 4px 4px 3px grey;
  -webkit-box-shadow: 4px 4px 3px grey;
}

@keyframes led {
  0% {
    transform: scale(1);
    opacity: 0.25;
  }
  50% {
    transform: scale (1, 5);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 0.25;
  }
}

.passageAlarm {
  position: absolute;
  left: 5px;
  top: 17px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: rgb(255, 0, 0);
  box-shadow: inset 0px 1px 0px rgba(0, 0, 0, 0.1);
  -webkit-animation: led 0.8s infinite;
  -moz-animation: led 0.8s infinite;
  -ms-animation: led 0.8s infinite;
  -o-animation: led 0.8s infinite;
  animation: led 0.8s infinite;
}

.detectCount {
  position: absolute;
  left: 20px;
  top: -4px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background-color: rgb(255, 255, 255);
  font-size: 9px;
  text-align: center;
  border-color: blue;
  border-width: 1px;
  border-style: solid;
  border-color: #666ff2;
  vertical-align: middle;
  margin-top: 2px;
  padding-top: 1px;
}

.heart {
  position: absolute;
  width: 10px;
  height: 9px;
  left: 4px;
  top: 17px;
}
.heart:before,
.heart:after {
  position: absolute;
  content: '';
  left: 5px;
  top: 0;
  width: 5px;
  height: 8px;
  background: #fc2e5a;
  -moz-border-radius: 50px 50px 0 0;
  border-radius: 50px 50px 0 0;
  -webkit-transform: rotate(-45deg);
  -moz-transform: rotate(-45deg);
  -ms-transform: rotate(-45deg);
  -o-transform: rotate(-45deg);
  transform: rotate(-45deg);
  -webkit-transform-origin: 0 100%;
  -moz-transform-origin: 0 100%;
  -ms-transform-origin: 0 100%;
  -o-transform-origin: 0 100%;
  transform-origin: 0 100%;
}
.heart:after {
  left: 0;
  -webkit-transform: rotate(45deg);
  -moz-transform: rotate(45deg);
  -ms-transform: rotate(45deg);
  -o-transform: rotate(45deg);
  transform: rotate(45deg);
  -webkit-transform-origin: 100% 100%;
  -moz-transform-origin: 100% 100%;
  -ms-transform-origin: 100% 100%;
  -o-transform-origin: 100% 100%;
  transform-origin: 100% 100%;
}

.leaflet-fa-markers {
  position: absolute;
  left: 0;
  top: 0;
  display: block;
  text-align: center;
  margin-left: -15px;
  margin-top: -50px;
  width: 30px;
  height: 50px;
}

.leaflet-fa-markers .marker-icon-svg {
  position: absolute;
}

.leaflet-fa-markers .feature-icon {
  position: absolute;
  font-size: 20px;
  line-height: 0px;
  left: 8px;
  top: 16px;
  display: inline-block;
}

.leaflet-container.crosshair-cursor-enabled {
  cursor: crosshair;
}

.pipeline-marker-default {
  width: 12px;
  height: 12px;
  background: #1976d2;
  border: 2px solid #fff;
  border-radius: 50%;
  box-shadow: 0 0 3px rgba(0, 0, 0, 0.4);
}

.pipeline-marker-complete {
  width: 12px;
  height: 12px;
  background: #4caf50;
  border: 2px solid #fff;
  border-radius: 50%;
  box-shadow: 0 0 3px rgba(0, 0, 0, 0.4);
}

.pipeline-marker-skipped {
  width: 12px;
  height: 12px;
  background: #9e9e9e;
  border: 2px solid #fff;
  border-radius: 50%;
  box-shadow: 0 0 3px rgba(0, 0, 0, 0.4);
}

.streamingIcon {
  position: absolute;
  left: 8px;
  top: 28px;
  font-size: 14px;
  line-height: 1;
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.6));
  pointer-events: none;
  animation: streamPulse 1.5s ease-in-out infinite;
}

@keyframes streamPulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}
</style>
